import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

// Components
import Layout from './components/common/Layout';
import LoginPage from './components/auth/LoginPage';
import Dashboard from './components/dashboard/Dashboard';
import Records from './components/records/Records';
import MonthlySummary from './components/monthlySummary/MonthlySummary';
import Categories from './components/categories/Categories';
import ExchangeRates from './components/exchange/ExchangeRates';
import ActivityLog from './components/audit/ActivityLog';
import DatabaseBackups from './components/backup/DatabaseBackups';

// Monthly Salary Components
import MonthlySalaryList from './components/monthlySalary/MonthlySalaryList';
import MonthlySalaryDetailPage from './components/monthlySalary/MonthlySalaryDetail';

// Payroll Components
import PayrollDashboard from './components/payroll/PayrollDashboard';
import EmployeeList from './components/payroll/EmployeeList';
import EmployeeDetail from './components/payroll/EmployeeDetail';
import EmployeeForm from './components/payroll/EmployeeForm';
import PayrollMonthList from './components/payroll/PayrollMonthList';
import PayrollMonthDetail from './components/payroll/PayrollMonthDetail';
import PayrollMonthForm from './components/payroll/PayrollMonthForm';
import SalaryProfileForm from './components/payroll/SalaryProfileForm';
import TaxBracketList from './components/payroll/TaxBracketList';

// Services
import { authService } from './services/api';

// Types
import { User } from './types';

// Styles
import './styles/App.css';

// Auth config
import { loginRequest, developmentConfig } from './config/authConfig';

// Loading component for authentication transitions
// Import the component directly
const LoadingSpinner = React.lazy(() => import('./components/common/LoadingSpinner'));

// Fallback loading component while the real one loads
const LoadingFallback = () => (
  <div className="loading-fallback">
    <div>Loading...</div>
  </div>
);

const App: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDevelopmentAuth, setIsDevelopmentAuth] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loginInProgress, setLoginInProgress] = useState<boolean>(false);

  // Handle authentication failures from API calls
  useEffect(() => {
    const handleAuthFailure = () => {
      console.warn('Authentication failure detected, redirecting to login');
      setAuthError('Your session has expired. Please log in again.');
      setCurrentUser(null);
      setLoginInProgress(false);
      setLoading(false);
      
      // If using real Microsoft authentication, log out from Microsoft
      if (!developmentConfig.enabled && isAuthenticated) {
        try {
          // First try to logout from our backend
          authService.logout().catch(err => console.error('Error logging out from backend:', err));
          // Then logout from Microsoft - use redirect for a cleaner experience
          instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin + '/login'
          });
        } catch (error) {
          console.error('Error during logout after auth failure:', error);
          // If logout fails, manually navigate to login
          navigate('/login');
        }
      } else if (developmentConfig.enabled) {
        // For mock auth, just reset the development auth state
        setIsDevelopmentAuth(false);
        navigate('/login');
      }
    };
    
    // Add event listener for auth failures
    window.addEventListener('auth:failed', handleAuthFailure);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('auth:failed', handleAuthFailure);
    };
  }, [instance, isAuthenticated, navigate]);

  // Define fetchUserProfile function before it's used in effects
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      console.log('Fetching user profile');
      
      if (developmentConfig.enabled) {
        console.log('Using development mock user');
        setCurrentUser(developmentConfig.mockUser);
        setIsDevelopmentAuth(true);
        setLoading(false);
        return;
      }

      // Ensure we have an active account set
      if (accounts.length > 0) {
        console.log('Setting active account for profile fetch:', accounts[0].username);
        instance.setActiveAccount(accounts[0]);
      } else {
        console.error('No accounts available for token acquisition');
        setLoading(false);
        return;
      }

      try {
        // Explicitly acquire token before API call
        console.log('Acquiring token for API call...');
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
        console.log('Token acquired successfully, token type:', tokenResponse.tokenType);

        // Now make the API call with the token
        console.log('Calling getCurrentUser API...');
        const response = await authService.getCurrentUser();
        console.log('User profile retrieved successfully:', response.data);
        
        setCurrentUser(response.data);
      } catch (tokenError) {
        console.error('Error acquiring token or fetching user profile:', tokenError);
        
        // Try interactive login as fallback
        try {
          console.log('Falling back to popup login...');
          await instance.acquireTokenPopup(loginRequest);
          const response = await authService.getCurrentUser();
          setCurrentUser(response.data);
        } catch (interactiveError) {
          console.error('Interactive token acquisition failed:', interactiveError);
          setAuthError('Failed to authenticate. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      setAuthError('Failed to load user profile');
    } finally {
      setLoading(false);
      setLoginInProgress(false);
    }
  };

  // Fetch user profile when authenticated
  useEffect(() => {
    if (isAuthenticated && !currentUser && !loading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, currentUser, loading, fetchUserProfile]);

  // Handle MSAL authentication state changes
  useEffect(() => {
    // Clear the redirect handling flag once we get here, as any redirect has been processed
    if (sessionStorage.getItem('msalHandlingRedirect') === 'true') {
      console.log('Clearing redirect handling flag');
      sessionStorage.removeItem('msalHandlingRedirect');
    }

    // Check for any auth errors that might have been stored during redirect
    const storedAuthError = sessionStorage.getItem('msalAuthError');
    if (storedAuthError) {
      try {
        const errorData = JSON.parse(storedAuthError);
        console.error('Auth error detected from previous redirect:', errorData);
        setAuthError(`Authentication error: ${errorData.errorMessage}`);
        sessionStorage.removeItem('msalAuthError');
      } catch (e) {
        console.error('Error parsing stored auth error:', e);
      }
    }
    
    if (isAuthenticated) {
      console.log('Authentication state change detected: User is authenticated');
      console.log('Available accounts:', accounts.map(a => a.username));
      console.log('Login in progress:', loginInProgress);
      
      // Set active account if available
      if (accounts.length > 0) {
        console.log('Setting active account to:', accounts[0].username);
        instance.setActiveAccount(accounts[0]);
        
        // Fetch user profile if not already done
        if (!currentUser && !loading) {
          console.log('No current user detected, fetching user profile');
          fetchUserProfile();
        }
      }
      
      // Only handle redirects if we're not currently in a login process
      if (!loginInProgress) {
        // If we're on the login page but authenticated, redirect appropriately
        if (window.location.pathname === '/login') {
          console.log('On login page while authenticated, redirecting...');
          
          // Check for a stored redirect path first
          const redirectPath = sessionStorage.getItem('redirectPath');
          if (redirectPath) {
            console.log('Redirecting to saved path after login:', redirectPath);
            sessionStorage.removeItem('redirectPath');
            navigate(redirectPath);
          } else {
            // Default to home page if no specific redirect
            console.log('No redirect path found, redirecting to home');
            navigate('/');
          }
        }
      }
    } else if (!isAuthenticated && accounts.length > 0) {
      // Strange state: We have accounts but not authenticated according to MSAL React
      console.warn('Unusual state: Not authenticated but has accounts:', accounts.length);
      
      // This can happen when tokens expire or are invalid
      // Try to re-authenticate silently with the existing account
      (async () => {
        try {
          console.log('Attempting silent token acquisition with existing account');
          const silentResult = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
          });
          
          if (silentResult) {
            console.log('Silent token acquisition successful');
            // Set the active account explicitly
            instance.setActiveAccount(accounts[0]);
          }
        } catch (error) {
          console.error('Silent token refresh failed:', error);
          // Clear any stale session data
          sessionStorage.removeItem('msalAuthenticated');
        }
      })();
    }
  }, [isAuthenticated, loginInProgress, navigate, accounts, instance, currentUser, loading, setAuthError, fetchUserProfile]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      // Clear any previous auth errors
      setAuthError(null);
      console.log('Fetching user profile - Auth state:', { 
        isAuthenticated, 
        accountsLength: accounts?.length || 0,
        isDevelopmentAuth,
        developmentEnabled: developmentConfig.enabled
      });
      
      // Check if we're using mock authentication
      if (developmentConfig.enabled) {
        console.log('Using mock authentication, setting development user');
        // In development mode, bypass Azure AD authentication and use mock user
        setCurrentUser(developmentConfig.mockUser);
        setIsDevelopmentAuth(true);
        setLoading(false);
        return;
      }

      // Only fetch user profile if authenticated
      if (isAuthenticated && accounts && accounts.length > 0) {
        try {
          // Set the active account
          console.log('Setting active account:', accounts[0].username);
          instance.setActiveAccount(accounts[0]);
          
          // Force token acquisition to ensure we have a valid token
          console.log('Acquiring token silently before API call');
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0]
            });
            console.log('Token acquired successfully:', {
              tokenLength: tokenResponse.accessToken?.length || 0,
              scopes: tokenResponse.scopes,
              expiresOn: tokenResponse.expiresOn
            });
          } catch (tokenError) {
            console.warn('Failed to acquire token silently:', tokenError);
            // Don't throw here, let the API call handle authentication
          }
          
          // Call the API to get the user's profile
          console.log('Calling getCurrentUser API endpoint');
          const response = await authService.getCurrentUser();
          console.log('User profile fetched successfully:', response.data);
          setCurrentUser(response.data);
          setLoginInProgress(false);
        } catch (error: any) { // Type error as any to handle response?.data safely
          console.error('Error fetching user profile:', error);
          // Check for specific API errors
          const apiError = error.response?.data?.message || 'Unknown error';
          console.error('API error details:', apiError);
          setAuthError(`Failed to fetch user profile: ${apiError}`);
          setLoginInProgress(false);
        } finally {
          setLoading(false);
        }
      } else {
        console.log('Not authenticated or no accounts available, skipping profile fetch');
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [isAuthenticated, accounts, instance, loginRequest, isDevelopmentAuth]);

  const handleLogin = async () => {
    setLoading(true);
    setAuthError(null);
    
    // Clear any previous auth errors
    sessionStorage.removeItem('msalAuthError');
    
    // Store current path for redirect after login
    const currentPath = window.location.pathname;
    const redirectPath = currentPath === '/login' ? '/' : currentPath;
    console.log('Saving redirect path before login:', redirectPath);
    sessionStorage.setItem('redirectPath', redirectPath);
    
    // Check if we are currently processing a redirect (to avoid loops)
    const isHandlingRedirect = sessionStorage.getItem('msalHandlingRedirect');
    if (isHandlingRedirect === 'true') {
      console.log('Already handling a redirect, preventing login loop');
      setLoading(false);
      return;
    }
    
    if (developmentConfig.enabled) {
      console.log('Using development mode authentication');
      // In development mode, just set the mock user
      setCurrentUser(developmentConfig.mockUser);
      setIsDevelopmentAuth(true);
      setLoading(false);
      return;
    }

    try {
      setLoginInProgress(true);
      console.log('Starting Microsoft authentication process with scopes:', loginRequest.scopes);
      
      // Check if there are any accounts already
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        console.log('Found existing accounts, trying to use them:', accounts.map(a => a.username));
        instance.setActiveAccount(accounts[0]);
        
        try {
          // Try to silently get a token with the existing account
          const silentResult = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
          });
          
          console.log('Silent token acquisition successful, token length:', silentResult.accessToken?.length || 0);
          
          // Fetch user profile to verify token works with backend
          await fetchUserProfile();
          setLoginInProgress(false);
          setLoading(false);
          return; // User is already authenticated
        } catch (silentError) {
          console.log('Silent token acquisition failed, proceeding with interactive login:', silentError);
          // Continue with interactive login below
        }
      }
      
      // No existing usable accounts, try interactive login
      try {
        console.log('Attempting login with popup');
        // Use a dedicated scope for the login to ensure we get a fresh token
        const loginReq = {
          ...loginRequest,
          prompt: 'select_account' // Force account selection to avoid cached credentials
        };
        
        const result = await instance.loginPopup(loginReq);
        console.log('Popup login successful, account:', result.account?.username);
        
        // Set this as the active account
        if (result.account) {
          instance.setActiveAccount(result.account);
          // Store a flag so we know we're authenticated
          sessionStorage.setItem('msalAuthenticated', 'true');
          await fetchUserProfile();
        }
        
        setLoginInProgress(false);
        setLoading(false);
      } catch (popupError) {
        console.warn('Popup login failed, falling back to redirect:', popupError);
        // Mark that we're starting a redirect flow to prevent loops
        sessionStorage.setItem('msalHandlingRedirect', 'true');
        // Fall back to redirect if popup fails
        console.log('Redirecting to Microsoft login page...');
        await instance.loginRedirect(loginRequest);
      }
    } catch (error) {
      console.error('Error during login:', error);
      setAuthError('An error occurred during login. Please try again.');
      sessionStorage.removeItem('msalHandlingRedirect');
      setLoginInProgress(false);
      setLoading(false);
    }
  };

  // Handle logout with option for local-only logout or complete Microsoft logout
  const handleLogout = async (localOnly: boolean = false) => {
    setLoading(true);
    
    if (developmentConfig.enabled) {
      // In development mode, just clear the user
      setCurrentUser(null);
      setIsDevelopmentAuth(false);
      setLoading(false);
      navigate('/login');
      return;
    }

    try {
      // Logout from our backend first
      await authService.logout().catch(err => console.error('Error logging out from backend:', err));
      
      // Clear user state and local auth data
      setCurrentUser(null);
      sessionStorage.removeItem('msalAuthenticated');
      
      // Reset MSAL state for our application
      try {
        // Set active account to null first
        instance.setActiveAccount(null);
        
        // Get all accounts and handle each one
        const accounts = instance.getAllAccounts();
        console.log(`Clearing MSAL state for ${accounts.length} accounts`);
        
        // For complete local signout, we'll use the native logout method with a
        // special flag to avoid redirect but still clear the cache
        if (accounts.length > 0) {
          instance.logout({
            onRedirectNavigate: () => {
              // Prevent the redirect to Microsoft by returning false
              return false;
            }
          }).catch(error => {
            console.warn('Error during local logout:', error);
          });
        }
      } catch (cacheError) {
        console.error('Error resetting MSAL state:', cacheError);
      }
      
      // If localOnly is true, don't sign out from Microsoft
      if (localOnly) {
        console.log('Local sign out completed - Microsoft account still active');
        setLoading(false);
        navigate('/login');
      } else {
        // Complete sign out from Microsoft
        console.log('Performing complete sign out from Microsoft');
        instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + '/login'
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
      setLoading(false);
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <React.Suspense fallback={<LoadingFallback />}>
          <LoadingSpinner size="large" message="Loading your profile..." />
        </React.Suspense>
      </div>
    );
  }

  // Determine if user is authenticated based on both the auth state and having a valid user profile
  // This ensures that if we have an authentication token but failed to fetch the user profile,
  // the user will be redirected to the login page
  const isUserAuthenticated = (developmentConfig.enabled ? isDevelopmentAuth : isAuthenticated) && currentUser !== null;

  return (
    <div className="app">
      {isUserAuthenticated ? (
        <Layout user={currentUser} onLogout={(localOnly = false) => handleLogout(localOnly)}>
          <Routes>
            <Route path="/" element={<Records />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/monthly-summary" element={<MonthlySummary />} />
            <Route path="/records" element={<Records />} />
            
            {/* Monthly Salary Routes */}
            <Route 
              path="/monthly-salary" 
              element={currentUser?.role === 'admin' ? <MonthlySalaryList /> : <Navigate to="/" />} 
            />
            <Route 
              path="/monthly-salary/:id" 
              element={currentUser?.role === 'admin' ? <MonthlySalaryDetailPage /> : <Navigate to="/" />} 
            />
            <Route 
              path="/categories" 
              element={currentUser?.role === 'admin' ? <Categories /> : <Navigate to="/" />} 
            />
            <Route 
              path="/exchange-rates" 
              element={currentUser?.role === 'admin' ? <ExchangeRates /> : <Navigate to="/" />} 
            />
            <Route 
              path="/activity-log" 
              element={currentUser?.role === 'admin' ? <ActivityLog /> : <Navigate to="/" />} 
            />
            <Route 
              path="/database-backups" 
              element={currentUser?.role === 'admin' ? <DatabaseBackups /> : <Navigate to="/" />} 
            />
            
            {/* Payroll Routes */}
            <Route 
              path="/payroll" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr' || currentUser?.role === 'accountant') ? <PayrollDashboard /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/employees" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr' || currentUser?.role === 'accountant') ? <EmployeeList /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/employees/new" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr') ? <EmployeeForm /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/employees/:id" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr' || currentUser?.role === 'accountant') ? <EmployeeDetail /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/employees/:id/edit" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr') ? <EmployeeForm /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/payroll-months" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr' || currentUser?.role === 'accountant') ? <PayrollMonthList /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/payroll-months/new" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr') ? <PayrollMonthForm /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/payroll-months/:id" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr' || currentUser?.role === 'accountant') ? <PayrollMonthDetail /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/payroll-months/:id/edit" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr') ? <PayrollMonthForm /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/salary-profiles" 
              element={(currentUser?.role === 'admin' || currentUser?.role === 'hr') ? <SalaryProfileForm /> : <Navigate to="/" />} 
            />
            <Route 
              path="/payroll/tax-brackets" 
              element={currentUser?.role === 'admin' ? <TaxBracketList /> : <Navigate to="/" />} 
            />
            
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Layout>
      ) : (
        <LoginPage onLogin={handleLogin} authError={authError} />
      )}
    </div>
  );
};

export default App;
